import clsx from "clsx";
import {
  Component,
  type ChangeEvent,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  memo,
  type MutableRefObject,
} from "react";
import { TeamLogoInline } from "../../components/TeamLogoInline.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import i18n from "../../util/i18n.ts";
import { helpers } from "../../util/helpers.ts";
import { toWorker } from "../../util/toWorker.ts";
import type { View } from "../../../../../common/types.ts";
import useLocalStorageState from "use-local-storage-state";
import { processLiveGameEvents } from "../../util/processLiveGameEvents.ts";
import { BoxScoreWrapper, HeadlineScoreLive } from "../../components/BoxScoreWrapper.tsx";
import { translateTeamShortName } from "../../util/teamNames.ts";
import { useIsStuck } from "../../hooks/useIsStuck.ts";
import { useBlocker } from "../../hooks/useBlocker.ts";
import { isMobileCoreLoopV4Enabled } from "../../util/mobileCoreLoopFeatureFlag.ts";
import { PlayPauseNext, type FastForward } from "../../components/PlayPauseNext.tsx";
import { MobileP3EventFeedback } from "../../mobile/MobileP3EventFeedback.tsx";
import { Confetti } from "../../components/Confetti.tsx";
import { BoxScoreRow } from "../../components/BoxScoreRow.tsx";
import { getPeriodName } from "../../util/getPeriodName.ts";
import {
  expandOvertimeAbbreviation,
  formatOvertimePeriodLabel,
} from "../../util/basketballLabels.ts";
import { notify } from "../../util/notify.ts";
import { confirm } from "../../util/confirm.tsx";
import router from "../../router/index.ts";
import { createLiveGameStatus } from "../../util/liveGameStatus.ts";
import { handleLiveGameExitClick, runLiveGameConfirmedAction } from "./confirmActions.ts";
import {
  getLiveGamePrimaryState,
  getLiveGameSchedulePath,
  getLiveGameShellContract,
} from "../gamesTemplate.ts";

type PlayerRowProps = {
  forceUpdate?: boolean;
  i: number;
  liveGameInProgress?: boolean;
  p: any;
  season: number;
};

class PlayerRow extends Component<PlayerRowProps> {
  prevInGame: boolean | undefined;

  // Can't just switch to hooks and React.memo because p is mutated, so there is no way to access the previous value of inGame in the memo callback function
  override shouldComponentUpdate(nextProps: PlayerRowProps) {
    return !!(this.prevInGame || nextProps.p.inGame || nextProps.forceUpdate);
  }

  override render() {
    const { p, ...props } = this.props;

    // Needed for shouldComponentUpdate because state is mutated so we need to explicitly store the last value
    this.prevInGame = p.inGame;

    const classes = clsx({
      "table-warning": p.inGame,
    });

    return <BoxScoreRow className={classes} p={p} {...props} />;
  }
}

const onLiveSimOver = () => {
  // Send to worker, rather than doing `localActions.update({ liveGameInProgress: false });`, so it works in all tabs
  toWorker("main", "onLiveSimOver", undefined);
};

const publishLiveGameStatus = (boxScore: any) => {
  const status = createLiveGameStatus(boxScore);
  if (status) {
    void toWorker("main", "updateLiveGameStatus", status);
  }
};

const getSeconds = (time: string | undefined) => {
  if (!time) {
    return 0;
  }

  const parts = time.split(":").map((x) => Number.parseInt(x));
  if (parts.length === 0) {
    return 0;
  }
  if (parts.length === 1) {
    // Seconds only being displayed
    return Number.parseFloat(time);
  }
  const [min, sec] = parts as [number, number];
  return min * 60 + sec;
};

const getLiveGameClockText = (boxScore: any) => {
  if (boxScore.gameOver) {
    return i18n.t("liveGame.finalStatus", { ns: "ui", defaultValue: "Final" });
  }

  if (boxScore.elamTarget !== undefined) {
    return i18n.t("liveGame.elamTargetDisplay", {
      ns: "ui",
      defaultValue: "Target: {{target}}",
      target: boxScore.elamTarget,
    });
  }

  const period = expandOvertimeAbbreviation(boxScore.quarterShort);
  return period && boxScore.time ? `${period} · ${boxScore.time}` : (boxScore.time ?? period);
};

const getLiveGameTeamLabel = (team: any) => translateTeamShortName(team, team.abbrev);

const getLiveGameTeamFouls = (team: any) =>
  Array.isArray(team.players)
    ? team.players.reduce((sum: number, player: any) => sum + (player.pf ?? 0), 0)
    : 0;

const liveGameSpeedPresets = [
  { labelKey: "liveGame.speedSlow", defaultLabel: "1x", value: "1" },
  { labelKey: "liveGame.speedNormal", defaultLabel: "2x", value: "7" },
  { labelKey: "liveGame.speedFast", defaultLabel: "4x", value: "13" },
] as const;

const getLiveGameLeaders = (boxScore: any) => {
  const players = (boxScore.teams ?? []).flatMap((team: any) =>
    (team.players ?? []).map((player: any) => ({
      ...player,
      abbrev: team.abbrev,
      teamLabel: getLiveGameTeamLabel(team),
    })),
  );

  return players
    .filter((player: any) => (player.min ?? 0) > 0 || player.pts > 0 || player.inGame)
    .sort((a: any, b: any) => helpers.gameScore(b) - helpers.gameScore(a))
    .slice(0, 3);
};

// C3: clock and possession moved into the score hero (法则二: each appears
// once); this block keeps the per-team timeout/foul situational state.
const LiveGameHeroDetails = ({ boxScore }: { boxScore: any }) => {
  const teams = boxScore.teams ?? [];

  return (
    <div className="live-game-hero-details">
      <div className="live-game-team-resource-grid">
        {teams.map((team: any) => (
          <div className="live-game-team-resource" key={team.tid}>
            <strong>{getLiveGameTeamLabel(team)}</strong>
            <span>
              {i18n.t("liveGame.timeoutsShort", {
                ns: "ui",
                count: team.timeouts ?? 0,
                defaultValue: "{{count}} TO",
              })}
              {" · "}
              {i18n.t("liveGame.foulsShort", {
                ns: "ui",
                count: getLiveGameTeamFouls(team),
                defaultValue: "{{count}} fouls",
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// C3 score hero: both logos, large tabular score, period/clock, and a
// possession dot, tinted with the two teams' primary colors (C2 color-mix
// precedent — low opacity over the theme background keeps contrast).
const LiveGameScoreHero = ({ boxScore, statusText }: { boxScore: any; statusText: string }) => {
  const teams = boxScore.teams ?? [];
  if (teams.length < 2) {
    return null;
  }

  const shootout = !!boxScore.shootout;
  const ptsKey = shootout ? "sPts" : "pts";
  const possession = typeof boxScore.possession === "number" ? boxScore.possession : undefined;
  const winnerIndex = boxScore.gameOver
    ? teams[0][ptsKey] > teams[1][ptsKey]
      ? 0
      : teams[1][ptsKey] > teams[0][ptsKey]
        ? 1
        : undefined
    : undefined;

  const heroStyle = {
    "--live-team-color-0": teams[0].colors?.[0],
    "--live-team-color-1": teams[1].colors?.[0],
  } as CSSProperties;

  return (
    <div
      className="live-game-score-hero d-md-none"
      data-v5-live-game-score-hero=""
      style={heroStyle}
    >
      <div className="live-game-score-hero-status small text-body-secondary text-uppercase">
        <span>{statusText}</span>
        {/* 法则二: when the game is over the status word already says final. */}
        {boxScore.gameOver ? null : (
          <strong className="text-body">{getLiveGameClockText(boxScore)}</strong>
        )}
        {boxScore.overtime ? <span>{boxScore.overtime}</span> : null}
      </div>
      <div className="live-game-score-hero-grid">
        {teams.map((team: any, i: number) => (
          <div className="live-game-score-hero-team" key={team.tid}>
            <TeamLogoInline
              alt={getLiveGameTeamLabel(team)}
              imgURL={team.imgURL}
              imgURLSmall={team.imgURLSmall}
              includePlaceholderIfNoLogo
              size={48}
            />
            <div className="live-game-score-hero-name text-truncate">
              {getLiveGameTeamLabel(team)}
              {possession === i && !boxScore.gameOver ? (
                <span
                  aria-label={i18n.t("liveGame.possessionValue", {
                    ns: "ui",
                    defaultValue: "{{team}} ball",
                    team: getLiveGameTeamLabel(team),
                  })}
                  className="live-game-possession-dot"
                  role="img"
                />
              ) : null}
            </div>
            <div
              className={clsx(
                "live-game-score-hero-points",
                winnerIndex !== undefined && winnerIndex !== i ? "text-body-secondary" : undefined,
              )}
            >
              {team[ptsKey] ?? 0}
              {shootout ? <span className="live-game-score-hero-sub">({team.pts})</span> : null}
            </div>
          </div>
        ))}
        <div aria-hidden="true" className="live-game-score-hero-divider">
          –
        </div>
      </div>
    </div>
  );
};

// C3: per-period score strip from the ptsQtrs data already on the live box
// score. Hidden until the first period has begun.
const LiveGameQuarterScores = ({ boxScore }: { boxScore: any }) => {
  const teams = boxScore.teams ?? [];
  const playedQtrs: number = Math.max(
    teams[0]?.ptsQtrs?.length ?? 0,
    teams[1]?.ptsQtrs?.length ?? 0,
  );

  if (teams.length < 2 || playedQtrs === 0) {
    return null;
  }

  const numPeriods: number = boxScore.numPeriods ?? playedQtrs;
  const numCols = Math.max(playedQtrs, numPeriods);
  const periodLabel = getPeriodName(numPeriods, true);
  const columns: string[] = [];
  for (let i = 0; i < numCols; i++) {
    columns.push(
      i < numPeriods ? `${periodLabel}${i + 1}` : formatOvertimePeriodLabel(i - numPeriods + 1),
    );
  }

  return (
    <div className="live-game-quarter-scores d-md-none" data-v5-live-game-quarter-scores="">
      <table className="live-game-quarter-table">
        <thead>
          <tr>
            <th scope="col">
              <span className="visually-hidden">
                {i18n.t("liveGame.quarterScores", {
                  ns: "ui",
                  defaultValue: "Score by period",
                })}
              </span>
            </th>
            {columns.map((label, i) => (
              <th key={i} scope="col">
                {label}
              </th>
            ))}
            <th className="live-game-quarter-total" scope="col">
              {i18n.t("liveGame.quarterTotal", { ns: "ui", defaultValue: "Total" })}
            </th>
          </tr>
        </thead>
        <tbody>
          {teams.map((team: any) => (
            <tr key={team.tid}>
              <th scope="row">{getLiveGameTeamLabel(team)}</th>
              {columns.map((_label, i) => (
                <td className={i === playedQtrs - 1 ? "fw-semibold" : undefined} key={i}>
                  {team.ptsQtrs?.[i] ?? "-"}
                </td>
              ))}
              <td className="live-game-quarter-total fw-bold">{team.pts ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const LiveGameKeyPlayers = ({ boxScore }: { boxScore: any }) => {
  const leaders = getLiveGameLeaders(boxScore);

  if (leaders.length === 0) {
    return null;
  }

  return (
    <div className="live-game-key-players">
      <div className="small fw-semibold text-body-secondary text-uppercase">
        {i18n.t("liveGame.keyPlayers", { ns: "ui", defaultValue: "Key players" })}
      </div>
      <div className="d-flex flex-column gap-1">
        {leaders.map((player: any) => (
          <div className="live-game-key-player" key={`${player.teamLabel}-${player.pid}`}>
            <strong>{player.name}</strong>
            <span className="text-body-secondary">
              {i18n.t("liveGame.keyPlayerLine", {
                ns: "ui",
                pts: player.pts ?? 0,
                reb: (player.orb ?? 0) + (player.drb ?? 0),
                ast: player.ast ?? 0,
                defaultValue: "{{pts}} pts · {{reb}} reb · {{ast}} ast",
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

type LiveGameMobileTab = "gamecast" | "boxScore" | "playByPlay" | "teamStats";

const liveGameMobileTabs = [
  {
    id: "gamecast",
    labelKey: "liveGame.tabGamecast",
    defaultLabel: "比赛动态",
  },
  {
    id: "boxScore",
    labelKey: "liveGame.tabBoxScore",
    defaultLabel: "技术统计",
  },
  {
    id: "playByPlay",
    labelKey: "liveGame.tabPlayByPlay",
    defaultLabel: "逐球记录",
  },
  {
    id: "teamStats",
    labelKey: "liveGame.tabTeamStats",
    defaultLabel: "球队数据",
  },
] as const satisfies readonly {
  defaultLabel: string;
  id: LiveGameMobileTab;
  labelKey: string;
}[];

// C3: the two vague folds (更多控制 / 更多比赛信息) merged into ONE
// settings/info sheet. Children are lazy-mounted (B2c precedent: closed
// <details> children still leak layout boxes into the audit).
const LiveGameSettingsSheet = ({
  activeTab,
  children,
  onSelect,
}: {
  activeTab: LiveGameMobileTab;
  children: ReactNode;
  onSelect: (tab: LiveGameMobileTab) => void;
}) => {
  const [open, setOpen] = useState(false);

  const activeTabConfig = liveGameMobileTabs.find((tab) => tab.id === activeTab)!;
  const activeTabLabel = i18n.t(activeTabConfig.labelKey, {
    ns: "ui",
    defaultValue: activeTabConfig.defaultLabel,
  });

  return (
    <details
      className="live-game-settings-sheet d-md-none"
      data-native-details-host="live-game-settings"
      data-v5-live-game-settings-sheet=""
      onToggle={(event) => {
        setOpen((event.currentTarget as HTMLDetailsElement).open);
      }}
      open={open}
    >
      <summary className="btn btn-light-bordered btn-sm w-100" data-live-game-settings-trigger="">
        {i18n.t("liveGame.gameSheet", {
          ns: "ui",
          section: activeTabLabel,
          defaultValue: "Settings & game info: {{section}}",
        })}
      </summary>
      {open ? (
        <div className="live-game-settings-sheet-body">
          <div
            aria-label={i18n.t("liveGame.mobileInfoTabs", {
              ns: "ui",
              defaultValue: "Live game sections",
            })}
            className="live-game-mobile-info-tabs"
            role="tablist"
          >
            {liveGameMobileTabs.map((tab) => (
              <button
                aria-controls={`live-game-mobile-panel-${tab.id}`}
                aria-selected={activeTab === tab.id}
                className={`btn btn-sm btn-${activeTab === tab.id ? "primary" : "light-bordered"}`}
                id={`live-game-mobile-tab-${tab.id}`}
                key={tab.id}
                onClick={() => {
                  onSelect(tab.id);
                  setOpen(false);
                }}
                role="tab"
                type="button"
              >
                {i18n.t(tab.labelKey, {
                  ns: "ui",
                  defaultValue: tab.defaultLabel,
                })}
              </button>
            ))}
          </div>
          {children}
        </div>
      ) : null}
    </details>
  );
};

const formatLiveGameStat = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const LiveGameTeamStats = ({ boxScore }: { boxScore: any }) => {
  const teams = boxScore.teams ?? [];
  const rows = [
    {
      key: "shooting",
      label: i18n.t("liveGame.teamStatShooting", {
        ns: "ui",
        defaultValue: "Shooting",
      }),
      value: (team: any) => `${formatLiveGameStat(team.fg)}-${formatLiveGameStat(team.fga)}`,
    },
    {
      key: "threePointers",
      label: i18n.t("liveGame.teamStatThrees", {
        ns: "ui",
        defaultValue: "3PT",
      }),
      value: (team: any) => `${formatLiveGameStat(team.tp)}-${formatLiveGameStat(team.tpa)}`,
    },
    {
      key: "rebounds",
      label: i18n.t("liveGame.teamStatRebounds", {
        ns: "ui",
        defaultValue: "Rebounds",
      }),
      value: (team: any) =>
        `${formatLiveGameStat(team.orb)}/${formatLiveGameStat(team.drb)}/${formatLiveGameStat(team.trb)}`,
    },
    {
      key: "assistsTurnovers",
      label: i18n.t("liveGame.teamStatAstTov", {
        ns: "ui",
        defaultValue: "AST/TOV",
      }),
      value: (team: any) => `${formatLiveGameStat(team.ast)}/${formatLiveGameStat(team.tov)}`,
    },
    {
      key: "freeThrows",
      label: i18n.t("liveGame.teamStatFreeThrows", {
        ns: "ui",
        defaultValue: "FT",
      }),
      value: (team: any) => `${formatLiveGameStat(team.ft)}-${formatLiveGameStat(team.fta)}`,
    },
  ];

  return (
    <section className="live-game-team-stats">
      <div className="small fw-semibold text-body-secondary text-uppercase mb-2">
        {i18n.t("liveGame.teamStatsTitle", {
          ns: "ui",
          defaultValue: "球队数据",
        })}
      </div>
      <div className="table-responsive">
        <table className="table table-sm mb-0">
          <thead>
            <tr>
              <th />
              {teams.map((team: any) => (
                <th key={team.tid}>{getLiveGameTeamLabel(team)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <th scope="row">{row.label}</th>
                {teams.map((team: any) => (
                  <td key={team.tid}>{row.value(team)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

// DEFAULT_SPORT_STATE removed - basketball has no sport state

// C3 key-play weighting: 3 = climax (clutch score / lead change / big run /
// game over), 2 = highlight (threes, dunk family, and-ones, blocks), 1 =
// ordinary content plays (other scores, steals, foul-outs, injuries), 0 =
// filler (misses, rebounds, subs, ordinary fouls).
type PlayWeight = 0 | 1 | 2 | 3;

const highlightPlayEventTypes = new Set([
  "tp",
  "tpAndOne",
  "fgAtRim",
  "fgAtRimAndOne",
  "fgTipIn",
  "fgTipInAndOne",
  "fgPutBack",
  "fgPutBackAndOne",
  "blkAtRim",
  "blkTipIn",
  "blkPutBack",
  "blkLowPost",
  "blkMidRange",
  "blkTp",
]);

const contentPlayEventTypes = new Set([
  "fgLowPost",
  "fgLowPostAndOne",
  "fgMidRange",
  "fgMidRangeAndOne",
  "ft",
  "stl",
  "foulOut",
  "injury",
]);

const CLUTCH_SECONDS = 120;
const CLUTCH_MARGIN = 5;
const BIG_RUN_PTS = 8;

const getPlayWeight = ({
  clockSeconds,
  eventType,
  leadChanged,
  margin,
  numPeriods,
  periodsPlayed,
  runPts,
  scored,
}: {
  clockSeconds: number;
  eventType: string | undefined;
  leadChanged: boolean;
  margin: number;
  numPeriods: number;
  periodsPlayed: number;
  runPts: number;
  scored: boolean;
}): PlayWeight => {
  if (eventType === "gameOver") {
    return 3;
  }

  if (scored) {
    // "Late" = final period or overtime; early lead changes are routine, so
    // only late ones grade as climax (early ones stay highlights).
    const late = periodsPlayed >= numPeriods;
    const clutch = late && clockSeconds <= CLUTCH_SECONDS && margin <= CLUTCH_MARGIN;
    if (clutch || (leadChanged && late) || runPts >= BIG_RUN_PTS) {
      return 3;
    }
  }

  if (
    (scored && leadChanged) ||
    (eventType !== undefined && highlightPlayEventTypes.has(eventType))
  ) {
    return 2;
  }

  if (scored || (eventType !== undefined && contentPlayEventTypes.has(eventType))) {
    return 1;
  }

  return 0;
};

type PlayByPlayEntryInfo = {
  eventType: string | undefined;
  key: number;
  score: ReactNode | undefined;
  t: 0 | 1 | undefined;
  text: ReactNode;
  textOnly: boolean;
  time: string;
  weight: PlayWeight;
};

const PlayByPlayEntry = memo(
  ({ boxScore, entry }: { boxScore: any; entry: PlayByPlayEntryInfo }) => {
    let scoreBlock = null;
    if (entry.score) {
      scoreBlock = entry.score;
    }

    return (
      <div
        className="d-flex"
        data-mobile-live-game-final-event={entry.eventType === "gameOver" ? "" : undefined}
        data-mobile-live-key-play-weight={entry.weight}
      >
        {entry.t !== undefined ? (
          <TeamLogoInline
            alt={translateTeamShortName(boxScore.teams[entry.t], boxScore.teams[entry.t].abbrev)}
            className={clsx("flex-shrink-0", {
              // If there is a time line, add a little top margin so logos align with the play text.
              "mt-1": !entry.textOnly && entry.time,
            })}
            imgURL={boxScore.teams[entry.t].imgURL}
            imgURLSmall={boxScore.teams[entry.t].imgURLSmall}
            includePlaceholderIfNoLogo
          />
        ) : null}
        <div
          className={clsx(
            "flex-grow-1 align-self-center me-2",
            entry.textOnly ? "fw-bold" : undefined,
            entry.t !== undefined ? "ms-2" : undefined,
          )}
        >
          {!entry.textOnly ? (
            <div className="d-flex">
              {entry.time ? <div className="text-body-secondary me-auto">{entry.time}</div> : null}
              {scoreBlock}
            </div>
          ) : null}
          {entry.text}
        </div>
      </div>
    );
  },
  () => true,
);

// C3 highlight feed shape: newest few always visible + weighted key plays,
// bounded so the gamecast stays one screen; the full stream lives in the
// play-by-play section of the settings sheet.
const HIGHLIGHT_RECENT_PLAYS = 3;
const HIGHLIGHT_MIN_WEIGHT: PlayWeight = 2;
const HIGHLIGHT_MAX_PLAYS = 12;

const PlayByPlay = ({
  autoFollow = false,
  boxScore,
  chronological = false,
  entries,
  highlightOnly,
  playByPlayDivRef,
}: {
  autoFollow?: boolean;
  boxScore: any;
  chronological?: boolean;
  entries: PlayByPlayEntryInfo[];
  /** C3: when true, show only weighted key plays + the most recent few. */
  highlightOnly?: boolean;
  playByPlayDivRef: MutableRefObject<HTMLDivElement | null>;
}) => {
  // Entries are newest-first; always keep the newest few so the feed visibly
  // scrolls while simulating, then weighted key plays and period headers.
  const visibleEntries = highlightOnly
    ? entries.filter(
        (e, i) => i < HIGHLIGHT_RECENT_PLAYS || e.textOnly || e.weight >= HIGHLIGHT_MIN_WEIGHT,
      )
    : entries;
  const displayedEntries = highlightOnly
    ? visibleEntries.slice(0, HIGHLIGHT_MAX_PLAYS)
    : visibleEntries;
  const renderedEntries = chronological ? [...displayedEntries].reverse() : displayedEntries;
  const latestEntryKey = displayedEntries[0]?.key;
  const hasAutoFollowed = useRef(false);

  useLayoutEffect(() => {
    const element = playByPlayDivRef.current;
    if (!autoFollow || !element || latestEntryKey === undefined) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      element.scrollTo({
        behavior: hasAutoFollowed.current && !reduceMotion ? "smooth" : "auto",
        top: element.scrollHeight,
      });
      hasAutoFollowed.current = true;
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [autoFollow, latestEntryKey, playByPlayDivRef]);

  useEffect(() => {
    const setPlayByPlayDivHeight = () => {
      if (playByPlayDivRef.current) {
        // Keep in sync with .live-game-affix
        if (window.matchMedia("(min-width:768px)").matches) {
          playByPlayDivRef.current.style.height = `${window.innerHeight - 113}px`;
        } else if (playByPlayDivRef.current.style.height !== "") {
          playByPlayDivRef.current.style.removeProperty("height");
        }
      }
    };

    // Keep height of plays list equal to window
    setPlayByPlayDivHeight();
    window.addEventListener("optimizedResize", setPlayByPlayDivHeight);

    return () => {
      window.removeEventListener("optimizedResize", setPlayByPlayDivHeight);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-mobile-live-game-event-flow={highlightOnly ? "highlights" : "full"}
      className="live-game-playbyplay d-flex flex-column gap-3"
      ref={playByPlayDivRef}
      style={{
        scrollMarginTop: 174,
      }}
    >
      <div className="live-game-playbyplay-header">
        <div className="fw-semibold">
          {i18n.t("liveGame.eventFlow", {
            ns: "ui",
            defaultValue: "Event flow",
          })}
        </div>
        <div className="small text-body-secondary">
          {i18n.t(highlightOnly ? "liveGame.highlightModeSummary" : "liveGame.fullModeSummary", {
            ns: "ui",
            count: visibleEntries.length,
            defaultValue: highlightOnly
              ? "Highlights, {{count}} plays"
              : "Full play-by-play, {{count}} plays",
          })}
        </div>
        {highlightOnly && displayedEntries.length > 0 ? (
          <div className="mt-1">
            <MobileP3EventFeedback event="liveKeyPlay">
              {i18n.t("liveGame.p3KeyPlayFeedback", {
                ns: "ui",
                defaultValue: "Key plays highlighted",
              })}
            </MobileP3EventFeedback>
          </div>
        ) : null}
      </div>
      {displayedEntries.length > 0 ? (
        renderedEntries.map((entry) => (
          <PlayByPlayEntry key={entry.key} boxScore={boxScore} entry={entry} />
        ))
      ) : (
        <div className="live-game-event-empty">
          {i18n.t("liveGame.noRecentPlays", {
            ns: "ui",
            defaultValue: "Recent plays will appear here as the game unfolds.",
          })}
        </div>
      )}
    </div>
  );
};

const DEFAULT_SPEED = 7;

const speedToMs = (speed: number) => {
  return 4000 / 1.2 ** speed;
};

const getNavigateWarning = () =>
  i18n.t("liveGame.navigateWarning", {
    ns: "ui",
    defaultValue:
      "If you leave this live view, pause first if you want to inspect the current play. The final result is already being simulated.",
  });

// C3: the red always-on warning paragraph is gone; the note shows once as a
// toast the first time the user pauses (localStorage key kept for backwards
// compatibility with the old dismissable paragraph).
const PAUSE_HINT_STORAGE_KEY = "showLiveSimWarning";

const showPauseHintOnce = () => {
  // The toast belongs to the v4 mobile core loop; the legacy flow keeps the
  // useBlocker exit confirmation only.
  if (!isMobileCoreLoopV4Enabled()) {
    return;
  }

  try {
    if (window.localStorage.getItem(PAUSE_HINT_STORAGE_KEY) === "false") {
      return;
    }
    window.localStorage.setItem(PAUSE_HINT_STORAGE_KEY, "false");
  } catch {
    // Storage unavailable: still show the hint, just not one-time.
  }

  notify(getNavigateWarning(), i18n.t("liveGame.title", { ns: "ui", defaultValue: "Live Game" }));
};

// C3: the duplicate pause/resume button and the double-nested speed fold are
// gone from here (法则二: the fixed bottom playback control is the single
// primary control; speed presets moved to the always-visible control row).
// What remains — show-next-play + skip-to-key-moment — lives in the merged
// settings sheet.
const LiveGameTextControls = ({
  disabled,
  fastForwards,
  onNext,
  paused,
}: {
  disabled?: boolean;
  fastForwards: FastForward[];
  onNext: () => void;
  paused: boolean;
}) => {
  const [confirmPending, setConfirmPending] = useState(false);
  const confirmPendingRef = useRef(false);

  const handleFastForward = async (item: FastForward) => {
    await runLiveGameConfirmedAction({
      confirmAction: () =>
        confirm(
          i18n.t("liveGame.skipConfirm", {
            ns: "ui",
            defaultValue: "Skip ahead? This advances the simulation to the selected moment.",
          }),
          { variant: "live-game-skip" },
        ),
      inFlight: confirmPendingRef,
      onConfirm: item.onClick,
      onPendingChange: setConfirmPending,
    });
  };

  return (
    <div className="live-game-text-controls d-md-none" data-mobile-live-game-controls>
      <div className="live-game-skip-menu">
        <div className="small fw-semibold text-body-secondary text-uppercase">
          {i18n.t("liveGame.skipToKeyMoment", {
            ns: "ui",
            defaultValue: "Skip to key moment",
          })}
        </div>
        <div className="live-game-skip-options">
          <button
            className="btn btn-sm btn-light-bordered"
            disabled={disabled || !paused || confirmPending}
            onClick={onNext}
            type="button"
          >
            {i18n.t("liveGame.showNextPlay", {
              ns: "ui",
              defaultValue: "Show Next Play",
            })}
          </button>
          {fastForwards.map((item, i) => (
            <button
              className="btn btn-sm btn-light-bordered"
              data-live-game-fast-forward-id={item.id}
              disabled={disabled || !paused || confirmPending}
              key={item.id ?? i}
              onClick={() => {
                void handleFastForward(item);
              }}
              type="button"
            >
              {item.label}
            </button>
          ))}
        </div>
        {!paused && !disabled ? (
          <div className="small text-body-secondary">
            {i18n.t("liveGame.skipNeedsPause", {
              ns: "ui",
              defaultValue: "Pause to skip ahead",
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

// C3: speed presets promoted out of the double fold onto the always-visible
// control row.
const LiveGameSpeedControl = ({
  disabled,
  onSpeedChange,
  speed,
}: {
  disabled?: boolean;
  onSpeedChange: (speed: string) => void;
  speed: string;
}) => (
  <div className="live-game-speed-inline">
    <span className="visually-hidden">
      {i18n.t("liveGame.speedSettings", {
        ns: "ui",
        defaultValue: "速度设置",
      })}
    </span>
    <div
      aria-label={i18n.t("liveGame.speedScale", { ns: "ui", defaultValue: "Speed" })}
      className="btn-group btn-group-sm"
      role="group"
    >
      {liveGameSpeedPresets.map((preset) => (
        <button
          aria-pressed={speed === preset.value}
          className={`btn btn-${speed === preset.value ? "primary" : "light-bordered"}`}
          data-live-game-speed-value={preset.value}
          disabled={disabled}
          key={preset.value}
          onClick={() => onSpeedChange(preset.value)}
          type="button"
        >
          {i18n.t(preset.labelKey, {
            ns: "ui",
            defaultValue: preset.defaultLabel,
          })}
        </button>
      ))}
    </div>
  </div>
);

const LiveGameBottomPlaybackControl = ({
  onPause,
  onPlay,
  paused,
}: {
  onPause: () => void;
  onPlay: () => void;
  paused: boolean;
}) => (
  <button
    className="btn btn-primary mobile-primary-cta-button live-game-bottom-playback-control"
    data-mobile-live-game-controls
    data-live-game-playback-action={paused ? "resume" : "pause"}
    onClick={paused ? onPlay : onPause}
    type="button"
  >
    {paused
      ? i18n.t("liveGame.resumeSimulation", {
          ns: "ui",
          defaultValue: "Resume Simulation",
        })
      : i18n.t("liveGame.pauseSimulation", {
          ns: "ui",
          defaultValue: "Pause Simulation",
        })}
  </button>
);

const LiveGameMobileTabPanel = ({
  activeTab,
  autoFollow,
  boxScore,
  mobilePlayByPlayDivRef,
  playByPlayEntries,
  playIndex,
  sportState,
}: {
  activeTab: LiveGameMobileTab;
  autoFollow: boolean;
  boxScore: any;
  mobilePlayByPlayDivRef: MutableRefObject<HTMLDivElement | null>;
  playByPlayEntries: PlayByPlayEntryInfo[];
  playIndex: number;
  sportState: any;
}) => (
  <section
    aria-labelledby={`live-game-mobile-tab-${activeTab}`}
    className="live-game-mobile-tab-panel d-md-none mb-3"
    id={`live-game-mobile-panel-${activeTab}`}
    role="tabpanel"
  >
    {activeTab === "gamecast" ? (
      <div className="live-game-gamecast-panel">
        <LiveGameKeyPlayers boxScore={boxScore} />
        <PlayByPlay
          autoFollow={autoFollow}
          boxScore={boxScore}
          chronological
          entries={playByPlayEntries}
          highlightOnly
          playByPlayDivRef={mobilePlayByPlayDivRef}
        />
      </div>
    ) : activeTab === "boxScore" ? (
      <div className="live-game-mobile-scroll-panel">
        <BoxScoreWrapper
          Row={PlayerRow}
          boxScore={boxScore}
          live
          playIndex={playIndex}
          sportState={sportState}
        />
      </div>
    ) : activeTab === "playByPlay" ? (
      <div className="live-game-mobile-scroll-panel">
        <PlayByPlay
          autoFollow={autoFollow}
          boxScore={boxScore}
          chronological
          entries={playByPlayEntries}
          highlightOnly={false}
          playByPlayDivRef={mobilePlayByPlayDivRef}
        />
      </div>
    ) : (
      <div className="live-game-mobile-scroll-panel">
        <LiveGameTeamStats boxScore={boxScore} />
      </div>
    )}
  </section>
);

export const LiveGame = (props: View<"liveGame">) => {
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(paused);
  const [speed, setSpeed] = useLocalStorageState("live-game-speed", {
    defaultValue: String(DEFAULT_SPEED),
  });
  const speedRef = useRef(Number.parseInt(speed));
  const [activeMobileTab, setActiveMobileTab] = useLocalStorageState<LiveGameMobileTab>(
    "live-game-mobile-tab",
    { defaultValue: "gamecast" },
  );
  const liveGameMode = activeMobileTab === "playByPlay" ? "full" : "highlight";
  const [playIndex, setPlayIndex] = useState(-1);
  const [started, setStarted] = useState(false);
  const [confetti, setConfetti] = useState<{
    colors?: [string, string, string];
    display: boolean;
  }>({
    display: false,
  });

  const boxScore = useRef<any>(props.initialBoxScore ? props.initialBoxScore : {});

  const overtimes = useRef(0);
  const playByPlayDiv = useRef<HTMLDivElement | null>(null);
  const mobilePlayByPlayDiv = useRef<HTMLDivElement | null>(null);
  const quarters = useRef<string[]>([]);
  const componentIsMounted = useRef(false);
  const events = useRef<any[] | undefined>(undefined);
  const sportState = useRef<any>(undefined);

  const playByPlayEntries = useRef<PlayByPlayEntryInfo[]>([]);

  // C3: consecutive-points run per team, for big-run key-play weighting.
  const scoringRun = useRef<{ pts: number; t: 0 | 1 | undefined }>({
    pts: 0,
    t: undefined,
  });

  const navigateWarning = getNavigateWarning();

  useEffect(() => {
    document.body.classList.add("mobile-live-game-active");
    window.scrollTo({ behavior: "auto", top: 0 });

    return () => {
      document.body.classList.remove("mobile-live-game-active");
    };
  }, []);

  const { setDirty } = useBlocker({
    message: navigateWarning,
    initialDirty: true,
    variant: "live-game-navigate",
  });

  // Make sure to call setPlayIndex after calling this! Can't be done inside because React is not always smart enough to batch renders
  const processToNextPause = useCallback(
    (force?: boolean): number => {
      if (!componentIsMounted.current || (pausedRef.current && !force) || !events.current) {
        return 0;
      }

      const startSeconds = getSeconds(boxScore.current.time);

      const shootout = !!boxScore.current.shootout;
      const ptsKey = shootout ? "sPts" : "pts";

      const prevPts = boxScore.current.teams[0][ptsKey] + boxScore.current.teams[1][ptsKey];
      const prevMargin = boxScore.current.teams[0][ptsKey] - boxScore.current.teams[1][ptsKey];

      const output = processLiveGameEvents({
        boxScore: boxScore.current,
        events: events.current,
        overtimes: overtimes.current,
        quarters: quarters.current,
        sportState: sportState.current,
      });
      const text = output.text;
      const currentPts = boxScore.current.teams[0][ptsKey] + boxScore.current.teams[1][ptsKey];
      const scoreDiff = currentPts - prevPts;

      overtimes.current = output.overtimes;
      quarters.current = output.quarters as string[];
      sportState.current = (output as any).sportState;

      if (text !== undefined) {
        const t = output.t;

        let score;
        if (scoreDiff !== 0) {
          const scoreT = t;

          score =
            scoreT === 0 ? (
              <>
                <b>{boxScore.current.teams[0][ptsKey]}</b>-
                <span className="text-body-secondary">{boxScore.current.teams[1][ptsKey]}</span>
              </>
            ) : scoreT === 1 ? (
              <>
                <span className="text-body-secondary">{boxScore.current.teams[0][ptsKey]}</span>-
                <b>{boxScore.current.teams[1][ptsKey]}</b>
              </>
            ) : undefined;
        }

        let time;
        if (shootout && t !== undefined) {
          time = i18n.t("liveGame.attempt", {
            ns: "ui",
            defaultValue: "Attempt {{att}}",
            att: boxScore.current.teams[t].sAtt,
          });
        } else if (boxScore.current.elamTarget !== undefined) {
          time = i18n.t("liveGame.elamTargetDisplay", {
            ns: "ui",
            defaultValue: "Target: {{target}}",
            target: boxScore.current.elamTarget,
          });
        } else {
          time = boxScore.current.time;
        }

        // C3 key-play weighting: scoring-run tracking + lead-change/clutch
        // detection feed getPlayWeight.
        const scored = scoreDiff !== 0;
        if (scored) {
          if (t !== undefined && scoringRun.current.t === t) {
            scoringRun.current.pts += scoreDiff;
          } else {
            scoringRun.current = { t, pts: scoreDiff };
          }
        }
        const newMargin = boxScore.current.teams[0][ptsKey] - boxScore.current.teams[1][ptsKey];
        const leadChanged =
          scored && ((prevMargin > 0 && newMargin <= 0) || (prevMargin < 0 && newMargin >= 0));
        const weight = getPlayWeight({
          clockSeconds: getSeconds(boxScore.current.time),
          eventType: (output as any).eventType,
          leadChanged,
          margin: Math.abs(newMargin),
          numPeriods: boxScore.current.numPeriods ?? 4,
          periodsPlayed: Math.max(
            boxScore.current.teams[0].ptsQtrs?.length ?? 0,
            boxScore.current.teams[1].ptsQtrs?.length ?? 0,
          ),
          runPts: scoringRun.current.pts,
          scored,
        });

        playByPlayEntries.current.unshift({
          eventType: (output as any).eventType,
          key: playByPlayEntries.current.length,
          score,
          text,
          textOnly: output.textOnly,
          t,
          time,
          weight,
        });
      }

      if (events.current && events.current.length > 0) {
        if (!pausedRef.current) {
          setTimeout(() => {
            processToNextPause();
            setPlayIndex((prev) => prev + 1);
          }, speedToMs(speedRef.current));
        }
      } else {
        boxScore.current.time = "0:00";
        boxScore.current.gameOver = true;
        boxScore.current.possession = undefined;

        // Update team records with result of game. Keep in sync with liveGame.ts.
        for (const t of boxScore.current.teams) {
          if (boxScore.current.playoffs) {
            if (t.playoffs) {
              if (boxScore.current.won.tid === t.tid) {
                t.playoffs.won += 1;

                if (props.confetti) {
                  setConfetti({
                    display: true,
                    colors: t.colors,
                  });
                }
              } else if (boxScore.current.lost.tid === t.tid) {
                t.playoffs.lost += 1;
              }
            }
          } else {
            if (
              boxScore.current.won.pts === boxScore.current.lost.pts &&
              boxScore.current.won.sPts === boxScore.current.lost.sPts
            ) {
              // Tied!
              if (t.tied !== undefined) {
                t.tied += 1;
              }
            } else if (boxScore.current.won.tid === t.tid) {
              t.won += 1;
            } else if (boxScore.current.lost.tid === t.tid) {
              if (boxScore.current.overtimes > 0 && props.otl) {
                t.otl += 1;
              } else {
                t.lost += 1;
              }
            }
          }
        }

        setDirty(false);
        onLiveSimOver();
      }

      publishLiveGameStatus(boxScore.current);

      const endSeconds = getSeconds(boxScore.current.time);

      // This is negative when rolling over to a new quarter
      const elapsedSeconds = startSeconds - endSeconds;
      return elapsedSeconds;
    },
    [props.confetti, props.otl, setDirty],
  );

  useEffect(() => {
    componentIsMounted.current = true;

    return () => {
      componentIsMounted.current = false;
      onLiveSimOver();
    };
  }, []);

  const startLiveGame = useCallback(
    (events2: any[]) => {
      events.current = events2;
      setTimeout(() => {
        processToNextPause();
        setPlayIndex((prev) => prev + 1);
      }, speedToMs(DEFAULT_SPEED));
    },
    [processToNextPause],
  );

  useEffect(() => {
    if (props.events && !started) {
      boxScore.current = props.initialBoxScore;
      publishLiveGameStatus(boxScore.current);
      setStarted(true);
      startLiveGame(props.events.slice());
    }
  }, [props.events, props.initialBoxScore, started, startLiveGame]);

  const handleSpeedChange = (event: ChangeEvent<HTMLInputElement>) => {
    const speed = event.target.value;
    setSpeed(speed);
    speedRef.current = Number.parseInt(speed);
  };
  const handleSpeedPresetChange = useCallback(
    (speed: string) => {
      setSpeed(speed);
      speedRef.current = Number.parseInt(speed);
    },
    [setSpeed],
  );

  const handlePause = useCallback(() => {
    setPaused(true);
    pausedRef.current = true;
    // C3: one-time hint replaces the old always-on red warning paragraph.
    showPauseHintOnce();
  }, []);

  const handlePlay = useCallback(() => {
    setPaused(false);

    // Without pausedRef check, this was a race condition and could lead to incorrect post-game records (counting as 2 or more wins)
    if (pausedRef.current) {
      pausedRef.current = false;
      processToNextPause();
    }

    setPlayIndex((prev) => prev + 1);
  }, [processToNextPause]);

  const handleNextPlay = useCallback(() => {
    processToNextPause(true);
    setPlayIndex((prev) => prev + 1);
  }, [processToNextPause]);

  const fastForwardMenuItems = useMemo(() => {
    // Plays up to `cutoffs` seconds, or until end of quarter
    const playSeconds = (cutoff: number) => {
      let seconds = 0;
      let numPlays = 0;

      // Stop at shootout, unless we're already in a shootout
      const initialShootout = boxScore.current.shootout;

      while (
        seconds < cutoff &&
        !boxScore.current.gameOver &&
        (initialShootout || !boxScore.current.shootout)
      ) {
        const elapsedSeconds = processToNextPause(true);
        numPlays += 1;
        if (elapsedSeconds > 0) {
          seconds += elapsedSeconds;
        } else if (elapsedSeconds < 0) {
          // End of quarter, always stop
          break;
        }
      }
      setPlayIndex((prev) => prev + numPlays);
    };

    const playUntilLastTwoMinutes = () => {
      // quarters.current.length can be 0 early in the game
      const initialQuarter = Math.max(1, quarters.current.length);

      const quartersToPlay =
        initialQuarter >= boxScore.current.numPeriods
          ? 0
          : boxScore.current.numPeriods - initialQuarter;
      for (let i = 0; i < quartersToPlay; i++) {
        playSeconds(Infinity);
      }

      const currentSeconds = getSeconds(boxScore.current.time);
      const targetSeconds = 125; // 2 minutes plus 5 seconds, cause can't always be exact
      const secoundsToPlay = currentSeconds - targetSeconds;
      if (secoundsToPlay > 0) {
        playSeconds(secoundsToPlay);
      }
    };

    const playUntilElamEnding = () => {
      let numPlays = 0;
      while (boxScore.current.elamTarget === undefined && !boxScore.current.gameOver) {
        processToNextPause(true);
        numPlays += 1;
      }
      setPlayIndex((prev) => prev + numPlays);
    };

    // elamTarget check is because clock is set to Infinity in Elam ending, so we can't skip ahead minutes
    let skipMinutes =
      boxScore.current.elamTarget !== undefined || boxScore.current.shootout
        ? []
        : [
            {
              minutes: 1,
            },
            {
              minutes: helpers.bound(Math.round(props.quarterLength / 4), 1, Infinity),
            },
            {
              minutes: helpers.bound(Math.round(props.quarterLength / 2), 1, Infinity),
            },
          ];

    // Dedupe
    const skipMinutesValues = new Set();
    skipMinutes = skipMinutes.filter(({ minutes }) => {
      if (skipMinutesValues.has(minutes)) {
        return false;
      }

      skipMinutesValues.add(minutes);
      return true;
    });

    const menuItems: FastForward[] = [
      ...skipMinutes.map(
        ({ minutes }) =>
          ({
            id: `minutes-${minutes}`,
            label: i18n.t("liveGame.minutes", {
              ns: "ui",
              count: minutes,
              defaultValue: "{{count}} minutes",
            }),
            onClick: () => {
              playSeconds(60 * minutes);
            },
          }) as FastForward,
      ),
      {
        id:
          boxScore.current.elamTarget !== undefined
            ? "endOfGame"
            : boxScore.current.shootout
              ? "endOfShootout"
              : "endOfPeriod",
        label:
          boxScore.current.elamTarget !== undefined
            ? i18n.t("liveGame.endOfGame", { ns: "ui", defaultValue: "End of game" })
            : boxScore.current.shootout
              ? i18n.t("liveGame.endOfShootout", { ns: "ui", defaultValue: "End of shootout" })
              : boxScore.current.overtime
                ? i18n.t("liveGame.endOfPeriod", { ns: "ui", defaultValue: "End of period" })
                : i18n.t("liveGame.endOf", {
                    ns: "ui",
                    defaultValue: "End of {{periodName}}",
                    periodName: getPeriodName(boxScore.current.numPeriods),
                  }),
        onClick: () => {
          playSeconds(Infinity);
        },
      },
    ];

    if (!boxScore.current.elam && !boxScore.current.shootout) {
      menuItems.push({
        id: "lastTwoMinutes",
        label: i18n.t("liveGame.last2Minutes", { ns: "ui", defaultValue: "Last 2 minutes" }),
        onClick: () => {
          playUntilLastTwoMinutes();
        },
      });
    }

    if (
      boxScore.current.elam &&
      !boxScore.current.elamOvertime &&
      boxScore.current.elamTarget === undefined
    ) {
      menuItems.push({
        id: "elamEnding",
        label: i18n.t("liveGame.elamEnding", { ns: "ui", defaultValue: "Elam Ending" }),
        onClick: () => {
          playUntilElamEnding();
        },
      });
    }

    return menuItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    boxScore.current.elam,
    boxScore.current.elamTarget,
    boxScore.current.overtime,
    boxScore.current.shootout,
    quarters.current.length,
    processToNextPause,
  ]);

  const scrollTop = useRef<HTMLDivElement>(null);

  const [liveGameStickyDiv, setLiveGameStickyDiv] = useState<HTMLElement | null>(null);
  const isStuck = useIsStuck(liveGameStickyDiv);
  const liveGameShell = getLiveGameShellContract({
    gameOver: boxScore.current.gameOver,
    mode: liveGameMode,
  });
  const liveGamePrimaryState = getLiveGamePrimaryState({
    gameOver: boxScore.current.gameOver,
  });
  const liveGameSchedulePath = getLiveGameSchedulePath({
    day: boxScore.current.day,
    season: boxScore.current.season,
  });
  const liveGameScheduleHref = helpers.leagueUrl(liveGameSchedulePath);
  const exitConfirmPendingRef = useRef(false);
  const handleExitLiveGame = async (event: ReactMouseEvent<HTMLAnchorElement>) => {
    await handleLiveGameExitClick({
      confirmExit: () =>
        confirm(
          i18n.t("liveGame.exitConfirm", {
            ns: "ui",
            defaultValue: "Exit live game? Pause first if you want to inspect the current play.",
          }),
          { variant: "live-game-exit" },
        ),
      event,
      gameOver: boxScore.current.gameOver,
      inFlight: exitConfirmPendingRef,
      navigate: async (target) => {
        // This confirmation is the navigation blocker for the explicit exit
        // control, so do not immediately show useBlocker's generic prompt too.
        setDirty(false);
        router.shouldBlock = undefined;
        await router.navigate(target);
      },
    });
  };
  const controlFeedbackKey = boxScore.current.gameOver
    ? "liveGame.controlFeedbackFinal"
    : paused
      ? "liveGame.controlFeedbackPaused"
      : "liveGame.controlFeedbackLive";
  const controlFeedbackDefault = boxScore.current.gameOver
    ? "Final"
    : paused
      ? "Paused"
      : "Simulating";
  const statusText = i18n.t(liveGamePrimaryState.statusKey, {
    ns: "ui",
    defaultValue: liveGamePrimaryState.defaultStatus,
  });

  // Needs to return actual div, not fragment, for AutoAffix!!!
  return (
    <div
      className="live-game-page"
      data-mobile-live-game-shell
      data-mobile-live-game-control-placement={liveGameShell.controlPlacement}
      data-mobile-live-game-exit-target={liveGameShell.exitTarget}
      data-mobile-live-game-mode={liveGameShell.mode}
      data-mobile-live-game-play-state={liveGameShell.playState}
      data-live-game-final={boxScore.current.gameOver ? "true" : "false"}
    >
      {confetti.display ? <Confetti colors={confetti.colors} /> : null}

      <div
        className="row live-game-page-row"
        ref={scrollTop}
        style={{
          scrollMarginTop: 174,
        }}
      >
        <div className="col-md-9 live-game-main-column">
          {boxScore.current.gid >= 0 ? (
            <div className="live-game-sticky mb-3" ref={setLiveGameStickyDiv}>
              <div
                className="pt-1 pt-md-0 live-game-score-wrapper"
                data-mobile-live-game-score-stage=""
              >
                <div className="d-none d-md-block">
                  <div
                    className="d-flex justify-content-between align-items-center mb-2"
                    data-desktop-live-game-exit-access=""
                  >
                    <a
                      className="btn btn-sm btn-light-bordered"
                      data-mobile-live-game-exit
                      href={helpers.leagueUrl(liveGameSchedulePath)}
                      onClick={(event) => {
                        void handleExitLiveGame(event);
                      }}
                    >
                      {i18n.t("liveGame.exitToMatchday", {
                        ns: "ui",
                        defaultValue: "Exit to matchday",
                      })}
                    </a>
                    <span className="small text-body-secondary">
                      {i18n.t("liveGame.fullscreenMode", {
                        ns: "ui",
                        defaultValue: "Fullscreen",
                      })}
                    </span>
                  </div>
                </div>
                <LiveGameScoreHero boxScore={boxScore.current} statusText={statusText} />
                <div className="d-none d-md-block">
                  <HeadlineScoreLive boxScore={boxScore.current} isStuck={isStuck} />
                </div>
                {boxScore.current.gameOver ? (
                  <div
                    className="live-game-postgame-actions d-none d-md-grid pt-2"
                    data-live-game-postgame-actions=""
                  >
                    <a
                      className="btn btn-primary"
                      data-live-game-postgame-action="recap"
                      href={helpers.leagueUrl(["game_log"])}
                    >
                      {i18n.t("liveGame.reviewRecap", {
                        ns: "ui",
                        defaultValue: "Review recap",
                      })}
                    </a>
                    <a
                      className="btn btn-light-bordered"
                      data-live-game-postgame-action="next"
                      href={liveGameScheduleHref}
                    >
                      {i18n.t(liveGamePrimaryState.nextAction.labelKey, {
                        ns: "ui",
                        defaultValue: liveGamePrimaryState.nextAction.defaultLabel,
                      })}
                    </a>
                    <a
                      className="btn btn-light-bordered"
                      data-live-game-postgame-action="home"
                      href={helpers.leagueUrl([])}
                    >
                      {i18n.t("liveGame.leagueHome", {
                        ns: "ui",
                        defaultValue: "League home",
                      })}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          {boxScore.current.gid >= 0 ? (
            <>
              <div className="live-game-mobile-workspace d-md-none">
                <div className="live-game-mobile-auxiliary">
                  <LiveGameQuarterScores boxScore={boxScore.current} />
                  <div
                    className="live-game-control-feedback"
                    data-mobile-live-game-control-feedback=""
                  >
                    <span>
                      {i18n.t(controlFeedbackKey, {
                        ns: "ui",
                        defaultValue: controlFeedbackDefault,
                      })}
                    </span>
                    <LiveGameSpeedControl
                      disabled={boxScore.current.gameOver}
                      onSpeedChange={handleSpeedPresetChange}
                      speed={speed}
                    />
                  </div>
                  <LiveGameSettingsSheet activeTab={activeMobileTab} onSelect={setActiveMobileTab}>
                    <LiveGameHeroDetails boxScore={boxScore.current} />
                    <LiveGameTextControls
                      disabled={boxScore.current.gameOver}
                      fastForwards={fastForwardMenuItems}
                      onNext={handleNextPlay}
                      paused={paused}
                    />
                  </LiveGameSettingsSheet>
                </div>
                <LiveGameMobileTabPanel
                  activeTab={activeMobileTab}
                  autoFollow={!paused}
                  boxScore={boxScore.current}
                  mobilePlayByPlayDivRef={mobilePlayByPlayDiv}
                  playByPlayEntries={playByPlayEntries.current}
                  playIndex={playIndex}
                  sportState={sportState.current}
                />
              </div>
              <div
                className={clsx("live-game-bottom-action-bar d-md-none", {
                  "live-game-bottom-action-bar-final": boxScore.current.gameOver,
                })}
                data-mobile-live-game-action-bar=""
              >
                {boxScore.current.gameOver ? null : (
                  <a
                    className="btn btn-light-bordered live-game-bottom-exit"
                    data-mobile-live-game-exit
                    href={liveGameScheduleHref}
                    onClick={(event) => {
                      void handleExitLiveGame(event);
                    }}
                  >
                    {i18n.t("liveGame.exitToMatchday", {
                      ns: "ui",
                      defaultValue: "Exit to matchday",
                    })}
                  </a>
                )}
                {boxScore.current.gameOver ? null : (
                  <LiveGameBottomPlaybackControl
                    onPause={handlePause}
                    onPlay={handlePlay}
                    paused={paused}
                  />
                )}
                {boxScore.current.gameOver ? (
                  <div
                    className="live-game-mobile-postgame-actions"
                    data-live-game-postgame-actions=""
                  >
                    <a
                      className="btn btn-primary"
                      data-live-game-postgame-action="recap"
                      href={helpers.leagueUrl(["game_log"])}
                    >
                      {i18n.t("liveGame.reviewRecap", {
                        ns: "ui",
                        defaultValue: "Review recap",
                      })}
                    </a>
                    <a
                      className="btn btn-light-bordered"
                      data-live-game-postgame-action="next"
                      href={liveGameScheduleHref}
                    >
                      {i18n.t(liveGamePrimaryState.nextAction.labelKey, {
                        ns: "ui",
                        defaultValue: liveGamePrimaryState.nextAction.defaultLabel,
                      })}
                    </a>
                    <a
                      className="btn btn-light-bordered"
                      data-live-game-postgame-action="home"
                      href={helpers.leagueUrl([])}
                    >
                      {i18n.t("liveGame.leagueHome", {
                        ns: "ui",
                        defaultValue: "League home",
                      })}
                    </a>
                  </div>
                ) : null}
              </div>
              <div className="d-none d-md-block">
                <BoxScoreWrapper
                  Row={PlayerRow}
                  boxScore={boxScore.current}
                  live
                  playIndex={playIndex}
                  sportState={sportState.current}
                />
              </div>
            </>
          ) : (
            <h2>{i18n.t("common.loading", { ns: "ui", defaultValue: "Loading..." })}</h2>
          )}
        </div>
        <div className="col-md-3 d-none d-md-block">
          <div className="live-game-affix">
            <div className="d-none d-md-flex align-items-center mb-3 pt-md-2">
              <PlayPauseNext
                className="me-2"
                disabled={boxScore.current.gameOver}
                fastForwardAlignRight
                fastForwards={fastForwardMenuItems}
                onPlay={handlePlay}
                onPause={handlePause}
                onNext={handleNextPlay}
                paused={paused}
                titlePlay={i18n.t("liveGame.resumeSimulation", {
                  ns: "ui",
                  defaultValue: "Resume Simulation",
                })}
                titlePause={i18n.t("liveGame.pauseSimulation", {
                  ns: "ui",
                  defaultValue: "Pause Simulation",
                })}
                titleNext={i18n.t("liveGame.showNextPlay", {
                  ns: "ui",
                  defaultValue: "Show Next Play",
                })}
              />
              <input
                type="range"
                className="form-range flex-grow-1"
                min="1"
                max="33"
                step="1"
                value={speed}
                onChange={handleSpeedChange}
                aria-label={i18n.t("liveGame.speed", { ns: "ui", defaultValue: "Speed" })}
              />
            </div>
            <PlayByPlay
              boxScore={boxScore.current}
              entries={playByPlayEntries.current}
              highlightOnly={liveGameMode === "highlight"}
              playByPlayDivRef={playByPlayDiv}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const LiveGameWrapper = (props: View<"liveGame">) => {
  useTitleBar({ title: i18n.t("liveGame.title", { ns: "ui", defaultValue: "Live Game" }) });

  return <LiveGame {...props} />;
};

export default LiveGameWrapper;
